import React from 'react';

import { formatExact, formatValue, memberLabel, monthOf, shortDay } from '../format';
import type { Envelope, EnvelopeColumn } from '../types';

type HeatmapProps = { envelope: Envelope };

export default function Heatmap({ envelope }: HeatmapProps) {
  const measure = envelope.columns.find((column) => column.role === 'measure') as EnvelopeColumn;
  const dims = envelope.columns.filter((column) => column.role === 'dim');
  const time = envelope.columns.find((column) => column.role === 'time');
  const across = time ? 't' : dims[0]?.name;
  const down = time ? dims[0]?.name : dims[1]?.name;
  const monthKey = /^\d{4}-\d{2}(-01)?$/;
  const columns = [...new Set(envelope.rows.map((row) => String(row[across ?? ''])))];
  const dated = across === 't' || (columns.length > 0 && columns.every((column) => monthKey.test(column)));
  const ordinal = (keys: Array<string>) =>
    keys.length > 0 && keys.every((key) => /^-?\d+(\.\d+)?$/.test(key)) ? Number : keys.length > 0 && keys.every((key) => /^[A-Z][a-z]{2} \d{4}$/.test(key)) ? (key: string) => Date.parse(`1 ${key}`) : undefined;
  const ordered = (keys: Array<string>) => {
    const rank = ordinal(keys);
    if (rank) keys.sort((a, b) => rank(a) - rank(b));
  };
  if (dated) columns.sort((a, b) => a.localeCompare(b));
  else ordered(columns);
  const spansYears = new Set(columns.map((column) => column.slice(0, 4))).size > 1;
  const label = (key: string | undefined, value: string) =>
    key === 't' && time?.label !== 'month' ? shortDay(value) : dated ? `${monthOf(`${value.slice(0, 7)}-01`)}${spansYears ? ` ’${value.slice(2, 4)}` : ''}` : memberLabel(value);
  const rows = down ? [...new Set(envelope.rows.map((row) => String(row[down])))] : [''];
  ordered(rows);
  const cell = new Map(envelope.rows.map((row) => [`${String(row[down ?? ''] ?? '')}|${String(row[across ?? ''])}`, row[measure.name] as number | null]));
  const largest = Math.max(...envelope.rows.map((row) => Math.abs((row[measure.name] as number | null) ?? 0)), 0) || 1;
  return (
    <div className="heat" style={{ gridTemplateColumns: `minmax(96px, max-content) repeat(${columns.length}, minmax(52px, 1fr))` }}>
      <span />
      {columns.map((column) => (
        <span className="hx" key={column}>
          {label(across, column)}
        </span>
      ))}
      {rows.map((row) => (
        <React.Fragment key={row}>
          <span className="hy" title={memberLabel(row)}>
            {memberLabel(row)}
          </span>
          {columns.map((column) => {
            const value = cell.get(`${row}|${column}`) ?? null;
            return (
              <span
                className="hc"
                data-lens-ref={`${envelope.query}.${measure.name}[${row}|${column}]`}
                key={column}
                style={{ '--heat': value === null ? 0 : Math.abs(value) / largest } as React.CSSProperties}
                title={formatExact(value, measure.format)}
              >
                {value === null ? '–' : formatValue(value, measure.format)}
              </span>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}
